import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import CongressSelector from "@/components/CongressSelector";
import LeadershipPanel from "@/components/LeadershipPanel";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Users, TrendingUp, Award, Crown } from "lucide-react";
import { toast } from "sonner";
import { readLegislatorCache, writeLegislatorCache } from "@/lib/legislatorCache";
import useScrollRestoration from "@/hooks/useScrollRestoration";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Legislators = () => {
  const initialCongress = (() => {
    const saved = localStorage.getItem("capitolPulse.congress");
    return saved ? parseInt(saved, 10) : 119;
  })();
  // Hydrate from sessionStorage on first render, avoids the empty-list
  // flash for return visits within the same tab.
  const cachedInitial = readLegislatorCache(initialCongress);
  const [legislators, setLegislators] = useState(cachedInitial || []);
  const [filteredLegislators, setFilteredLegislators] = useState(cachedInitial || []);
  const [loading, setLoading] = useState(!cachedInitial);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterChamber, setFilterChamber] = useState("all");
  const [filterParty, setFilterParty] = useState("all");
  const [congress, setCongress] = useState(initialCongress);

  // Restore scroll on browser-back from a legislator profile. The directory
  // is one of the longest scrolling surfaces in the app, losing position
  // every time the user dives into a profile makes the experience feel
  // jumpy. Key is per-congress so House and Senate filters get their own.
  const { saveScroll } = useScrollRestoration(
    `legislators:${congress}`,
    !loading && legislators.length > 0
  );

  useEffect(() => {
    fetchLegislators(congress);
    localStorage.setItem("capitolPulse.congress", String(congress));
  }, [congress]);

  useEffect(() => {
    applyFilters();
  }, [legislators, searchQuery, filterChamber, filterParty]);

  const fetchLegislators = async (targetCongress) => {
    // If we already have a warm cache for this congress, skip the spinner and
    // refresh silently in the background, otherwise show the spinner.
    const cached = readLegislatorCache(targetCongress);
    if (cached) {
      setLegislators(cached);
      setFilteredLegislators(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/legislators`, { params: { congress: targetCongress } });
      setLegislators(response.data);
      setFilteredLegislators(response.data);
      writeLegislatorCache(targetCongress, response.data);
    } catch (error) {
      // If we had cached data rendered, don't interrupt the user with a toast.
      if (!cached) toast.error("Failed to load legislators");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...legislators];

    if (searchQuery) {
      result = result.filter(
        (leg) =>
          leg.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          leg.state.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterChamber !== "all") {
      // API returns chamber as "Senate" or "House of Representatives"; the
      // filter tab uses short labels ("Senate" / "House"). Normalize both
      // sides so the comparison works regardless of format.
      const norm = (c) => {
        const s = (c || "").toLowerCase();
        if (s.startsWith("senate")) return "senate";
        if (s.startsWith("house")) return "house";
        return s;
      };
      const want = norm(filterChamber);
      result = result.filter((leg) => norm(leg.chamber) === want);
    }

    if (filterParty !== "all") {
      result = result.filter((leg) => leg.party === filterParty);
    }

    setFilteredLegislators(result);
  };

  const getPartyColor = (party) => {
    const colors = {
      D: "bg-blue-100 text-blue-800 border-blue-200",
      R: "bg-red-100 text-red-800 border-red-200",
      I: "bg-purple-100 text-purple-800 border-purple-200",
    };
    return colors[party] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getPartyName = (party) => {
    const names = { D: "Democrat", R: "Republican", I: "Independent" };
    return names[party] || party;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading legislators...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <h1 className="text-4xl font-bold text-gray-900" data-testid="legislators-title">
                Legislators
              </h1>
              <p className="text-base text-gray-600 mt-2">U.S. Senators and Representatives</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <CongressSelector value={congress} onChange={setCongress} testId="legislators-congress-selector" />
              <Badge variant="outline" className="px-4 py-2 text-sm border-2">
                <Users className="w-4 h-4 mr-2" />
                {legislators.length} Members
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 119th Congress leadership, shows House+Senate or just one based on
            the chamber filter. Data comes from /api/leadership. */}
        <LeadershipPanel chamberFilter={filterChamber} />

        {/* Search and Filters */}
        <div className="mb-8">
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              data-testid="search-legislators"
              type="text"
              placeholder="Search by name or state..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-base rounded-xl border-2 focus:ring-2 focus:ring-blue-500 transition-all"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <Tabs defaultValue="all-chamber" className="mb-2">
              <TabsList className="inline-flex flex-wrap h-auto gap-2">
                <TabsTrigger
                  data-testid="filter-all-chamber"
                  value="all-chamber"
                  onClick={() => setFilterChamber("all")}
                  className="data-[state=active]:whip-orange-bg data-[state=active]:text-white text-white/65"
                >
                  All Chambers
                </TabsTrigger>
                <TabsTrigger
                  data-testid="filter-senate"
                  value="senate"
                  onClick={() => setFilterChamber("Senate")}
                  className="data-[state=active]:whip-orange-bg data-[state=active]:text-white text-white/65"
                >
                  Senate
                </TabsTrigger>
                <TabsTrigger
                  data-testid="filter-house"
                  value="house"
                  onClick={() => setFilterChamber("House")}
                  className="data-[state=active]:whip-orange-bg data-[state=active]:text-white text-white/65"
                >
                  House
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Tabs defaultValue="all-party" className="mb-2">
              <TabsList className="inline-flex flex-wrap h-auto gap-2">
                <TabsTrigger
                  data-testid="filter-all-party"
                  value="all-party"
                  onClick={() => setFilterParty("all")}
                  className="data-[state=active]:whip-orange-bg data-[state=active]:text-white text-white/65"
                >
                  All Parties
                </TabsTrigger>
                <TabsTrigger
                  data-testid="filter-democrat"
                  value="democrat"
                  onClick={() => setFilterParty("D")}
                  className="data-[state=active]:whip-orange-bg data-[state=active]:text-white text-white/65"
                >
                  Democrat
                </TabsTrigger>
                <TabsTrigger
                  data-testid="filter-republican"
                  value="republican"
                  onClick={() => setFilterParty("R")}
                  className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
                >
                  Republican
                </TabsTrigger>
                <TabsTrigger
                  data-testid="filter-independent"
                  value="independent"
                  onClick={() => setFilterParty("I")}
                  className="data-[state=active]:whip-orange-bg data-[state=active]:text-white text-white/65"
                >
                  Independent
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600" data-testid="legislators-count">
            Showing <span className="font-semibold text-gray-900">{filteredLegislators.length}</span> of{" "}
            <span className="font-semibold text-gray-900">{legislators.length}</span> legislators
          </p>
        </div>

        {/* Legislators Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredLegislators.map((legislator) => (
            <Link key={legislator.id} to={`/legislator/${legislator.id}`} className="block h-full" onClick={saveScroll}>
            <Card
              data-testid={`legislator-card-${legislator.id}`}
              className="p-6 h-full flex flex-col hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-blue-400 cursor-pointer bg-white rounded-xl"
            >
              {/* Profile Header (fixed height so every card matches) */}
              <div className="flex items-start gap-4 mb-4 h-28 overflow-hidden">
                <div className="relative">
                  <img
                    src={legislator.photo}
                    alt={legislator.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-gray-100"
                  />
                  <div
                    className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full ${getPartyColor(
                      legislator.party
                    )} border-2 border-white flex items-center justify-center text-xs font-bold`}
                  >
                    {legislator.party}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 truncate">{legislator.name}</h3>
                  <p className="text-sm text-gray-600">
                    {legislator.state} • {legislator.chamber}
                  </p>
                  {legislator.district && (
                    <p className="text-xs text-gray-500">{legislator.district}</p>
                  )}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-orange-400" />
                    <p className="text-xs text-orange-400 font-medium">Bills Sponsored</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{legislator.stats.billsSponsored}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-green-600" />
                    <p className="text-xs text-green-600 font-medium">Bills Enacted</p>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{legislator.stats.billsPassed}</p>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Voting Record</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {((legislator.stats.votesCast / legislator.stats.votesCount) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-600">Bipartisan Index</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {legislator.stats.bipartisanIndex.toFixed(1)}
                  </span>
                </div>
              </div>

              {/* Committees (pinned to the bottom so every card is the same height) */}
              <div className="flex flex-wrap gap-1 mt-auto pt-2 h-[4.75rem] overflow-hidden content-start">
                {legislator.leadershipRole && (
                  <Badge className="text-xs px-2 py-1 bg-orange-500/15 text-orange-300 border border-orange-500/40" data-testid={`leadership-chip-${legislator.id}`}>
                    <Crown className="w-3 h-3 mr-1" /> {legislator.leadershipRole}
                  </Badge>
                )}
                {legislator.committees.slice(0, 2).map((committee) => (
                  <Badge key={committee} variant="outline" className="text-xs px-2 py-1">
                    {committee}
                  </Badge>
                ))}
                {legislator.committees.length > 2 && (
                  <Badge variant="outline" className="text-xs px-2 py-1">
                    +{legislator.committees.length - 2}
                  </Badge>
                )}
              </div>
            </Card>
            </Link>
          ))}
        </div>

        {filteredLegislators.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl text-gray-600">No legislators found</p>
            <p className="text-gray-500 mt-2">Try adjusting your search or filters</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Legislators;
